import { Button, Flex, Modal, message } from "antd"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"

import { requestPaperCopy, withdrawConsent } from "../api/signing.services"
import type { SigningRefusal } from "../types/signing.types"

interface Props {
  session: string
  isSigner: boolean
  onWithdrawn: (refusal: SigningRefusal) => void
}

/**
 * Asking for paper, and taking the agreement back.
 *
 * Buttons rather than an address to write to, because the statute wants a route
 * that works — and an email to the contractor is something this product can
 * neither see nor record. They sit beside every step after the inbox is proved:
 * a right that disappears once the document is on screen is not a right.
 *
 * Only a signer is offered the withdrawal. A viewer is never asked to agree to
 * anything, so there is nothing of theirs to take back.
 */
const ConsumerRights: React.FC<Props> = ({
  session,
  isSigner,
  onWithdrawn
}) => {
  const { t } = useTranslation()

  const askForPaper = useCallback(async () => {
    const response = await requestPaperCopy(session)
    void (response.ok
      ? message.success(t("signing.consent.paperRequested"))
      : message.error(t("signing.consent.paperFailed")))
  }, [session, t])

  const confirmWithdrawal = useCallback(() => {
    Modal.confirm({
      title: t("signing.consent.withdrawTitle"),
      content: t("signing.consent.withdrawExplain"),
      okText: t("signing.consent.withdrawConfirm"),
      cancelText: t("signing.cancel"),
      okButtonProps: { danger: true },
      onOk: async () => {
        const response = await withdrawConsent(session)
        onWithdrawn(
          response.ok
            ? { reason: "consent_withdrawn" }
            : (response.refusal ?? { reason: "unknown" })
        )
      }
    })
  }, [session, onWithdrawn, t])

  return (
    <Flex gap={8} wrap justify="space-between">
      <Button
        type="link"
        style={{ minHeight: 44 }}
        onClick={() => void askForPaper()}
      >
        {t("signing.consent.paperCopy")}
      </Button>
      {isSigner && (
        <Button
          type="link"
          danger
          style={{ minHeight: 44 }}
          onClick={confirmWithdrawal}
        >
          {t("signing.consent.withdraw")}
        </Button>
      )}
    </Flex>
  )
}

export default ConsumerRights
